// Multi-location print area editor. Each location has a name, a draggable
// bounding box (PrintAreaEditor), and an optional per-location mockup image
// that overrides the product-level one (e.g. a back-of-shirt photo for the
// Back location). Saved as a full array via the parent's onChange.
import { useRef, useState, useCallback, useEffect } from 'react'
import PrintAreaEditor, { PrintAreaValue, DEFAULT_PRINT_AREA, MockupCalibration } from './PrintAreaEditor'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { AI_TOOLS_ENABLED, DISPLAY_MOCKUP_PER_LOCATION_ENABLED } from '../../lib/features'

export interface NormBox { left: number; top: number; width: number; height: number }

export interface PrintLocation extends PrintAreaValue {
  id: string
  name: string
  sortOrder: number
  mockupFileId: string | null
  mockupUrl: string | null
  aiReferenceFileId: string | null
  aiReferenceUrl: string | null
  aiReferenceBox: NormBox | null
  mockupCalibration: MockupCalibration | null
}

function newLocId(): string {
  return Math.random().toString(36).slice(2, 14)
}

// Promise wrapper around <img> load so the sync handler can await reference
// dimensions before computing the placement box.
function loadImageDims(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = () => reject(new Error('Reference image failed to load'))
    img.src = src
  })
}

const PRESETS = ['Front', 'Back', 'Left Sleeve', 'Right Sleeve', 'Left Chest', 'Right Chest', 'Hem'] as const

export default function PrintLocationsEditor({
  productId, productMockupUrl, locations, onChange,
  activeLocationId, colours, sideColourMockups,
}: {
  productId: string
  productMockupUrl: string | null
  locations: PrintLocation[]
  onChange: (next: PrintLocation[]) => void
  activeLocationId?: string | null
  colours?: Array<{ id: string; name: string; hex: string; mockupUrl?: string | null }>
  sideColourMockups?: Array<{ locationId: string; colourId: string; url: string }>
}) {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [openId, setOpenId] = useState<string | null>(locations[0]?.id ?? null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [uploadingFor, setUploadingFor] = useState<string | null>(null)
  const [calibColourId, setCalibColourId] = useState<string | null>(null)

  // Sync the active tab whenever the mockup card's side selection changes.
  useEffect(() => {
    if (activeLocationId && locations.some((l) => l.id === activeLocationId)) {
      setOpenId(activeLocationId)
    }
  }, [activeLocationId]) // eslint-disable-line react-hooks/exhaustive-deps

  function update(id: string, patch: Partial<PrintLocation>) {
    onChange(locations.map((l) => l.id === id ? { ...l, ...patch } : l))
  }

  // Pull the yellow print-area dimensions into the purple AI-placement box.
  //
  // Naive "fraction of canvas → fraction of reference" doesn't work because
  // the Designer canvas is 640×720 (≈square) and reference photos are
  // usually landscape — a vertical yellow box (e.g. 200×260) maps to a
  // *horizontal* purple box on a landscape reference. To preserve the
  // yellow box's SHAPE we:
  //   1. Read the reference image's native pixel dimensions.
  //   2. Anchor by the yellow box's dominant axis (height for portrait
  //      prints, width for landscape ones) so the box keeps the same
  //      footprint relative to the garment in the photo.
  //   3. Derive the other axis from the yellow box's pixel aspect ratio,
  //      converted into reference-image normalized space.
  async function syncBoxSizeFromPrintArea(l: PrintLocation) {
    if (!l.aiReferenceUrl) return
    const CANVAS_W = 640, CANVAS_H = 720
    const yellowAspect = l.width / l.height // pixel aspect ratio of the yellow box

    // Load reference dims so we can convert aspect-correctly. Falls back to
    // a 1:1 assumption if the image won't load (the user will see slightly
    // wrong proportions and can still adjust by hand).
    const refDims = await loadImageDims(apiSrc(l.aiReferenceUrl)).catch(() => ({ w: 1, h: 1 }))
    const refAspect = refDims.w / refDims.h

    // Anchor: portrait yellow box → anchor by height; landscape → by width.
    // Same-fraction-of-canvas keeps the print area at a familiar scale.
    const portrait = l.height >= l.width
    let width: number, height: number
    if (portrait) {
      height = Math.min(1, l.height / CANVAS_H)
      // width in reference pixels = height in reference pixels × yellowAspect
      // → normalized: (height * refH * yellowAspect) / refW = height * yellowAspect / refAspect
      width = Math.min(1, (height * yellowAspect) / refAspect)
    } else {
      width = Math.min(1, l.width / CANVAS_W)
      // height = width / yellowAspect (in pixels) → normalized
      height = Math.min(1, (width * refAspect) / yellowAspect)
    }

    const existing = l.aiReferenceBox
    const cx = existing ? (existing.left + existing.width  / 2) : 0.5
    const cy = existing ? (existing.top  + existing.height / 2) : 0.5
    const left = Math.max(0, Math.min(1 - width,  cx - width  / 2))
    const top  = Math.max(0, Math.min(1 - height, cy - height / 2))
    update(l.id, { aiReferenceBox: { left, top, width, height } })
  }
  function addLocation(name = 'New location') {
    const loc: PrintLocation = {
      ...DEFAULT_PRINT_AREA,
      id: newLocId(),
      name,
      sortOrder: locations.length,
      mockupFileId: null,
      mockupUrl: null,
      aiReferenceFileId: null,
      aiReferenceUrl: null,
      aiReferenceBox: null,
      mockupCalibration: null,
    }
    onChange([...locations, loc])
    setOpenId(loc.id)
  }
  function removeLocation(id: string) {
    if (!confirm('Remove this location? Customer designs for it will be archived on next save.')) return
    onChange(locations.filter((l) => l.id !== id))
    if (openId === id) setOpenId(locations[0]?.id ?? null)
  }

  async function uploadLocationFile(locationId: string, file: File, target: 'mockup' | 'ai_reference', productId: string) {
    setUploadingFor(`${locationId}:${target}`)
    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const up = await apiFetch<{ id: string; url: string }>(
          getAccessToken as unknown as () => Promise<string | undefined>,
          '/api/files',
          { method: 'POST', body: JSON.stringify({ originalName: file.name, mimeType: file.type, base64: reader.result, kind: target, productId }) },
        )
        if (target === 'mockup') update(locationId, { mockupFileId: up.id, mockupUrl: up.url })
        else                     update(locationId, { aiReferenceFileId: up.id, aiReferenceUrl: up.url })
        setUploadingFor(null)
      }
      reader.readAsDataURL(file)
    } catch (e) {
      setUploadingFor(null)
      alert((e as Error).message)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[11px] text-ink-3">
          {locations.length} location{locations.length === 1 ? '' : 's'} configured.
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {PRESETS.filter((p) => !locations.some((l) => l.name === p)).map((p) => (
            <button key={p} onClick={() => addLocation(p)}
              className="text-[11px] text-ink-2 hover:text-gold-dark px-2 py-1 border border-border rounded hover:border-gold">
              + {p}
            </button>
          ))}
          <button onClick={() => addLocation()} className="btn-gold text-xs px-3 py-1.5">+ Custom</button>
        </div>
      </div>

      {/* Tab strip — one tab per location */}
      <div className="flex flex-wrap gap-1.5 border-b border-border mb-3">
        {locations.map((l) => (
          <button key={l.id} onClick={() => setOpenId(l.id)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ' +
              (openId === l.id ? 'border-b-2 border-gold text-ink' : 'text-ink-3 hover:text-ink')}>
            {l.name}
          </button>
        ))}
      </div>

      {locations.length === 0 && (
        <div className="card text-center text-ink-3 text-sm">Add at least one location (e.g. Front).</div>
      )}

      {locations.map((l) => l.id !== openId ? null : (
        <div key={l.id} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="eyebrow">Name</div>
              <input value={l.name} onChange={(e) => update(l.id, { name: e.target.value })} className="input" />
            </div>
            <div>
              <div className="eyebrow">Sort order</div>
              <input type="number" value={l.sortOrder} onChange={(e) => update(l.id, { sortOrder: parseInt(e.target.value) || 0 })} className="input" />
            </div>
          </div>

          <div className={'grid gap-3 ' + ((AI_TOOLS_ENABLED && DISPLAY_MOCKUP_PER_LOCATION_ENABLED) ? 'md:grid-cols-2' : 'md:grid-cols-1')}>
            {/* Mockup — visible in the Designer behind the canvas. Hidden
                by feature flag (defaults to off) since the product-level
                mockup is enough for most garments and the per-location
                override was rarely used. State + handlers stay so flipping
                the flag back on restores the upload card with no other
                changes needed. */}
            {DISPLAY_MOCKUP_PER_LOCATION_ENABLED && (
            <div className="border border-border rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow !mb-0">Display mockup</div>
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLocationFile(l.id, f, 'mockup', productId); e.target.value = '' }} />
                  <button onClick={() => fileRef.current?.click()} className="text-xs text-gold-dark hover:underline">
                    {uploadingFor === `${l.id}:mockup` ? 'Uploading…' : l.mockupUrl ? 'Replace' : 'Upload'}
                  </button>
                  {l.mockupUrl && (
                    <button onClick={() => update(l.id, { mockupFileId: null, mockupUrl: null })} className="text-xs text-ink-3 hover:text-red-600">Clear</button>
                  )}
                </div>
              </div>
              <div className="aspect-video bg-surface border border-border rounded overflow-hidden flex items-center justify-center mb-2">
                {l.mockupUrl ? <img src={apiSrc(l.mockupUrl)} className="max-w-full max-h-full object-contain" alt="" /> : <span className="text-[10px] text-ink-3 uppercase">no mockup</span>}
              </div>
              <p className="text-[11px] text-ink-3">Shown behind the Designer canvas. Clean garment photo, no design placeholder.</p>
            </div>
            )}

            {/* AI reference — never visible in the Designer; only sent to AI Preview. */}
            {AI_TOOLS_ENABLED && (
            <div className="border border-border rounded p-3 bg-purple-50/30" style={{ borderColor: 'rgba(126,34,206,0.2)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow !mb-0" style={{ color: '#7E22CE' }}>AI reference</div>
                <div className="flex items-center gap-2">
                  <input id={`ai-ref-${l.id}`} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLocationFile(l.id, f, 'ai_reference', productId); e.target.value = '' }} />
                  <button onClick={() => document.getElementById(`ai-ref-${l.id}`)?.click()} className="text-xs hover:underline" style={{ color: '#7E22CE' }}>
                    {uploadingFor === `${l.id}:ai_reference` ? 'Uploading…' : l.aiReferenceUrl ? 'Replace' : 'Upload'}
                  </button>
                  {l.aiReferenceUrl && (
                    <button onClick={() => update(l.id, { aiReferenceFileId: null, aiReferenceUrl: null })} className="text-xs text-ink-3 hover:text-red-600">Clear</button>
                  )}
                </div>
              </div>
              <div className="aspect-video bg-surface border border-border rounded overflow-hidden flex items-center justify-center mb-2">
                {l.aiReferenceUrl ? <img src={apiSrc(l.aiReferenceUrl)} className="max-w-full max-h-full object-contain" alt="" /> : <span className="text-[10px] text-ink-3 uppercase">no reference</span>}
              </div>
              <p className="text-[11px] text-ink-3">Hidden from the Designer. Used only by AI Preview as the conditioning image — fine to have "YOUR DESIGN HERE" placeholders, wrinkles, etc.</p>
            </div>
            )}
          </div>

          {/* AI placement box — temporarily hidden 2026-05-29 while we trial
              the text-description-based generation approach (preview model
              generates from the per-garment scene prompt + location name
              rather than compositing onto a reference image). The state
              field (aiReferenceBox), zod, normalize helpers, and editor
              components are intentionally left in place so flipping the
              flag back is a one-line change if/when we revert. */}
          {false && l.aiReferenceUrl && (
            <div className="border border-border rounded p-3" style={{ borderColor: 'rgba(126,34,206,0.2)', background: 'rgba(250,245,255,0.5)' }}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="eyebrow !mb-0" style={{ color: '#7E22CE' }}>AI placement box</div>
                  <p className="text-[11px] text-ink-3 mt-0.5">Drag the purple box onto the spot on this garment photo where the design should appear. Corners resize, body moves.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => void syncBoxSizeFromPrintArea(l)} className="text-[11px] px-2 py-1 rounded border" style={{ color: '#7E22CE', borderColor: 'rgba(126,34,206,0.3)' }}>
                    ↻ Sync size from print area
                  </button>
                  {l.aiReferenceBox && (
                    <button onClick={() => update(l.id, { aiReferenceBox: null })} className="text-[11px] text-ink-3 hover:text-red-600">Clear box</button>
                  )}
                </div>
              </div>
              <NormalizedBoxEditor
                imageUrl={apiSrc(l.aiReferenceUrl)}
                value={l.aiReferenceBox}
                onChange={(box) => update(l.id, { aiReferenceBox: box })}
              />
              <BoxCoordinates
                value={l.aiReferenceBox}
                printArea={{ left: l.left, top: l.top, width: l.width, height: l.height }}
                imageUrl={apiSrc(l.aiReferenceUrl)}
              />
            </div>
          )}

          <PrintAreaEditor
            colourSelector={colours && colours.length > 0 ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 shrink-0">Colour</span>
                <select
                  value={calibColourId ?? ''}
                  onChange={(e) => setCalibColourId(e.target.value || null)}
                  className="input !text-xs !py-1 !w-auto"
                >
                  <option value="">Default</option>
                  {colours.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {calibColourId && colours.find((c) => c.id === calibColourId) && (
                  <span className="w-3.5 h-3.5 rounded-full border border-ink-4 shrink-0 inline-block"
                    style={{ background: colours.find((c) => c.id === calibColourId)!.hex }} />
                )}
              </div>
            ) : undefined}
            mockupUrl={(() => {
              if (l.mockupUrl) return apiSrc(l.mockupUrl)
              if (calibColourId && sideColourMockups) {
                const sc = sideColourMockups.find((m) => m.locationId === l.id && m.colourId === calibColourId)
                if (sc) return apiSrc(sc.url)
              }
              if (calibColourId && colours) {
                const col = colours.find((c) => c.id === calibColourId)
                if (col?.mockupUrl) return apiSrc(col.mockupUrl)
              }
              return productMockupUrl
            })()}
            value={{ left: l.left, top: l.top, width: l.width, height: l.height }}
            onChange={(next) => update(l.id, next)}
            locationName={l.name}
            mockupCalibration={l.mockupCalibration}
            onMockupCalibrationChange={(next) => update(l.id, { mockupCalibration: next })}
            // Combined writer so Apply / Clear inside PrintAreaEditor
            // commit the rebased position AND the new calibration in
            // a single update() — otherwise React batches the two
            // sequential update()s and the position update is silently
            // overwritten by the calibration patch (which closes over
            // the pre-rebase location), producing the "shift on save"
            // the admin keeps hitting.
            onSaveCalibrationCombined={(rebased, calib) =>
              update(l.id, { ...rebased, mockupCalibration: calib })
            }
          />

          <div className="border-t border-border pt-3 flex justify-end">
            <button onClick={() => removeLocation(l.id)} className="text-xs text-red-600 hover:underline">Remove this location</button>
          </div>
        </div>
      ))}
    </div>
  )
}

// Editor that overlays a normalized (0..1) rectangle on the AI reference image
// and lets the admin drag/resize it. The image is shown contained inside an
// aspect-ratio box; the rectangle is positioned in % so it's resolution-free.
//
// First-paint default (when value is null): a vertical print-zone style box,
// centred horizontally and biased toward the upper half — the most common
// case for chest prints on hanging/laid-flat garment photos.
const DEFAULT_BOX: NormBox = { left: 0.36, top: 0.20, width: 0.28, height: 0.36 }

type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se'

function NormalizedBoxEditor({ imageUrl, value, onChange }: {
  imageUrl: string
  value: NormBox | null
  onChange: (next: NormBox) => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Whether the user has positioned the box yet. If `value` is null we show
  // the default but don't commit it until they actually drag.
  const box = value ?? DEFAULT_BOX

  const beginDrag = useCallback((e: React.PointerEvent, handle: Handle) => {
    const container = containerRef.current
    if (!container) return
    e.preventDefault(); e.stopPropagation()
    const rect = container.getBoundingClientRect()
    const startX = e.clientX, startY = e.clientY
    const start = box
    function clamp(v: number, lo: number, hi: number) { return Math.min(hi, Math.max(lo, v)) }

    function onMove(ev: PointerEvent) {
      const dx = (ev.clientX - startX) / rect.width
      const dy = (ev.clientY - startY) / rect.height
      let { left, top, width, height } = start
      if (handle === 'move') {
        left = clamp(start.left + dx, 0, 1 - start.width)
        top  = clamp(start.top  + dy, 0, 1 - start.height)
      } else {
        // Corner resize. Anchor opposite corner.
        const right = start.left + start.width
        const bottom = start.top + start.height
        if (handle === 'nw') {
          left = clamp(start.left + dx, 0, right - 0.04)
          top  = clamp(start.top  + dy, 0, bottom - 0.04)
          width  = right - left
          height = bottom - top
        } else if (handle === 'ne') {
          width  = clamp(start.width + dx, 0.04, 1 - start.left)
          top    = clamp(start.top   + dy, 0, bottom - 0.04)
          height = bottom - top
        } else if (handle === 'sw') {
          left   = clamp(start.left + dx, 0, right - 0.04)
          height = clamp(start.height + dy, 0.04, 1 - start.top)
          width  = right - left
        } else if (handle === 'se') {
          width  = clamp(start.width  + dx, 0.04, 1 - start.left)
          height = clamp(start.height + dy, 0.04, 1 - start.top)
        }
      }
      onChange({ left, top, width, height })
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [box, onChange])

  return (
    <div ref={containerRef} className="relative bg-surface border border-border rounded overflow-hidden select-none"
      style={{ aspectRatio: '16 / 10' }}>
      <img src={imageUrl} alt="AI reference" className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
      <div
        onPointerDown={(e) => beginDrag(e, 'move')}
        className="absolute border-2 border-dashed cursor-move"
        style={{
          left: (box.left * 100) + '%',
          top: (box.top * 100) + '%',
          width: (box.width * 100) + '%',
          height: (box.height * 100) + '%',
          borderColor: '#7E22CE',
          background: 'rgba(126,34,206,0.10)',
        }}
        title="Drag to move the AI placement box. Drag a corner to resize."
      >
        <span className="absolute -top-[18px] left-0 text-[10px] uppercase tracking-wider font-bold px-1 rounded" style={{ background: '#7E22CE', color: '#fff' }}>
          PLACE DESIGN HERE
        </span>
        {(['nw','ne','sw','se'] as Handle[]).map((h) => (
          <div key={h}
            onPointerDown={(e) => beginDrag(e, h)}
            className="absolute w-3 h-3 bg-white border-2"
            style={{
              borderColor: '#7E22CE',
              cursor: h === 'nw' || h === 'se' ? 'nwse-resize' : 'nesw-resize',
              top:    h.startsWith('n') ? '-6px' : 'auto',
              bottom: h.startsWith('s') ? '-6px' : 'auto',
              left:   h.endsWith('w')   ? '-6px' : 'auto',
              right:  h.endsWith('e')   ? '-6px' : 'auto',
            }}
          />
        ))}
      </div>
      {!value && (
        <div className="absolute bottom-1 left-1 right-1 text-center text-[10px] uppercase tracking-wider text-ink-3 bg-white/80 rounded py-0.5">
          Drag the box to set placement — it will save on the next location save.
        </div>
      )}
    </div>
  )
}

// Live coordinate readout under the placement-box editor. Shows the values
// that actually get saved (normalized 0..1 ratios, displayed as percentages)
// plus the equivalent absolute pixel offsets relative to the reference
// image's native dimensions and the matching print-area numbers from the
// Designer canvas — easy verification when admin wants the boxes "the same".
function BoxCoordinates({ value, printArea, imageUrl }: {
  value: NormBox | null
  printArea: { left: number; top: number; width: number; height: number }
  imageUrl: string
}) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  // Read the reference's native pixel size once it's available — used only
  // for the absolute-pixel readout. The normalized values are independent.
  useEffect(() => {
    let cancelled = false
    const img = new Image()
    img.onload = () => { if (!cancelled) setDims({ w: img.naturalWidth, h: img.naturalHeight }) }
    img.src = imageUrl
    return () => { cancelled = true }
  }, [imageUrl])

  const pct = (n: number) => (n * 100).toFixed(1) + '%'

  return (
    <div className="mt-2 text-[11px] text-ink-3 font-mono leading-snug bg-white border border-border rounded p-2">
      {value ? (
        <>
          <div className="grid grid-cols-2 gap-x-4">
            <div>
              <span className="text-ink-2 font-semibold">Position</span>
              <span> · left {pct(value.left)} · top {pct(value.top)}</span>
              {dims && (
                <span className="text-ink-3"> · {Math.round(value.left * dims.w)}×{Math.round(value.top * dims.h)} px</span>
              )}
            </div>
            <div>
              <span className="text-ink-2 font-semibold">Size</span>
              <span> · {pct(value.width)} × {pct(value.height)}</span>
              {dims && (
                <span className="text-ink-3"> · {Math.round(value.width * dims.w)}×{Math.round(value.height * dims.h)} px</span>
              )}
            </div>
          </div>
          <div className="mt-1 text-ink-3">
            <span className="text-ink-2">Print area (yellow)</span> · {printArea.width}×{printArea.height} px on 640×720 canvas · aspect {(printArea.width / printArea.height).toFixed(2)}
            {dims && (() => {
              // Compare pixel-aspect of the two boxes (purple is in reference
              // pixels; yellow is in canvas pixels). Match within 3% is good
              // enough — within visual tolerance of a hand-drawn box.
              const yellowAspect = printArea.width / printArea.height
              const purpleAspect = (value.width * dims.w) / (value.height * dims.h)
              const drift = Math.abs(purpleAspect - yellowAspect) / yellowAspect
              return drift < 0.03
                ? <span className="ml-2 text-emerald-700">✓ aspect synced</span>
                : <span className="ml-2 text-amber-700">aspect drift {(drift * 100).toFixed(0)}%</span>
            })()}
          </div>
        </>
      ) : (
        <span>No box set — click <strong className="text-ink-2">Sync size from print area</strong> to start, then drag it into position on the reference photo.</span>
      )}
    </div>
  )
}
